/**
 * Extra publishing options shown in the composer when they apply:
 *  - VideoCoverPicker: the cover of a reel / video, as a frame of the video
 *    (extracted server-side) or an image from the library.
 *  - CollaboratorsInput: Instagram collaborators for feed posts and reels,
 *    collapsed by default; each username is looked up to show its avatar.
 */
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BadgeCheck, ChevronDown, Image as ImageIcon, Loader2, Users, X, Film } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { cn } from '../lib/cn';

export type VideoCover = { videoId: string; url: string; ms: number | null };

type LibraryImage = { id: string; url: string; thumbnailUrl: string | null };

const fmtTime = (ms: number) => {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

export function VideoCoverPicker(props: {
  video: { id: string; url: string };
  images: LibraryImage[];
  value: VideoCover | null;
  onChange: (v: VideoCover | null) => void;
}) {
  const { video, images, value, onChange } = props;
  const videoRef = useRef<HTMLVideoElement>(null);
  const [durationMs, setDurationMs] = useState(0);
  const [ms, setMs] = useState(value?.ms ?? 0);
  const [mode, setMode] = useState<'frame' | 'image'>(value && value.ms === null ? 'image' : 'frame');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seek the preview whenever the slider moves.
  useEffect(() => {
    const el = videoRef.current;
    if (el && Number.isFinite(el.duration)) el.currentTime = ms / 1000;
  }, [ms]);

  async function saveFrame() {
    setSaving(true);
    setError(null);
    try {
      const res = await api<{ url: string; ms: number }>(`/api/media/${video.id}/cover-frame`, {
        method: 'POST',
        body: JSON.stringify({ ms: Math.round(ms) }),
      });
      onChange({ videoId: video.id, url: res.url, ms: res.ms });
    } catch (err) {
      setError(err instanceof ApiError && err.message ? err.message : 'No se pudo guardar la portada.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-xl border border-slate-200 p-3">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <span className="text-sm font-medium text-slate-700">Portada del vídeo</span>
        <div className="flex bg-slate-100 rounded-lg p-0.5 text-xs font-medium">
          {(['frame', 'image'] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={cn(
                'px-2.5 py-1 rounded-md transition inline-flex items-center gap-1',
                mode === m ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500',
              )}
            >
              {m === 'frame' ? <Film size={12} /> : <ImageIcon size={12} />}
              {m === 'frame' ? 'Fotograma' : 'Imagen'}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-3 items-start">
        <div className="w-20 shrink-0">
          <div className="aspect-[9/16] rounded-lg overflow-hidden bg-slate-100 border border-slate-200">
            {value ? (
              <img src={value.url} alt="Portada elegida" className="h-full w-full object-cover" />
            ) : (
              <div className="h-full w-full flex items-center justify-center text-center text-[10px] text-slate-400 px-1">
                La elige la red
              </div>
            )}
          </div>
          {value && (
            <button
              type="button"
              onClick={() => onChange(null)}
              className="mt-1 w-full text-[11px] text-slate-500 hover:text-red-600 transition"
            >
              Quitar
            </button>
          )}
        </div>

        <div className="flex-1 min-w-0">
          {mode === 'frame' ? (
            <>
              <video
                ref={videoRef}
                src={video.url}
                muted
                playsInline
                preload="metadata"
                onLoadedMetadata={(e) => {
                  const d = e.currentTarget.duration;
                  if (Number.isFinite(d)) setDurationMs(Math.floor(d * 1000));
                  e.currentTarget.currentTime = ms / 1000;
                }}
                className="w-full max-h-48 rounded-lg bg-black object-contain"
              />
              <div className="mt-2 flex items-center gap-2">
                <input
                  type="range"
                  min={0}
                  max={Math.max(0, durationMs - 100)}
                  step={100}
                  value={ms}
                  onChange={(e) => setMs(Number(e.target.value))}
                  disabled={durationMs === 0}
                  aria-label="Momento del vídeo para la portada"
                  className="flex-1 accent-brand-600"
                />
                <span className="text-xs tabular-nums text-slate-500 w-10 text-right">{fmtTime(ms)}</span>
              </div>
              <button
                type="button"
                onClick={saveFrame}
                disabled={saving || durationMs === 0}
                className="mt-2 inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md bg-brand-600 text-white font-medium hover:bg-brand-700 transition disabled:opacity-60"
              >
                {saving && <Loader2 size={12} className="animate-spin" />}
                {saving ? 'Guardando…' : 'Usar este fotograma'}
              </button>
            </>
          ) : images.length === 0 ? (
            <p className="text-xs text-slate-500">
              Sube una imagen a tu biblioteca (en la columna de archivos) para usarla como portada.
            </p>
          ) : (
            <div className="grid grid-cols-4 sm:grid-cols-5 gap-1.5 max-h-40 overflow-y-auto">
              {images.map((img) => (
                <button
                  key={img.id}
                  type="button"
                  onClick={() => onChange({ videoId: video.id, url: img.url, ms: null })}
                  className={cn(
                    'aspect-square rounded-md overflow-hidden border-2 transition',
                    value?.url === img.url ? 'border-brand-600' : 'border-transparent hover:border-slate-300',
                  )}
                >
                  <img src={img.thumbnailUrl ?? img.url} alt="" loading="lazy" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
          {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
        </div>
      </div>
      <p className="mt-2 text-[11px] text-slate-400">
        Se usa en Instagram, Facebook, TikTok y YouTube. En las stories no se aplica.
      </p>
    </div>
  );
}

const USERNAME = /^[A-Za-z0-9._]{1,30}$/;
const MAX_COLLABORATORS = 3;

type IgProfile = {
  username: string;
  fullName: string | null;
  avatar: string | null;
  isVerified: boolean;
  isPrivate: boolean;
};

/** Profile by exact username; resolves to null when the account doesn't exist. */
function useIgProfile(username: string | null) {
  return useQuery({
    queryKey: ['ig-profile', username?.toLowerCase()],
    queryFn: async () => {
      try {
        const res = await api<{ profile: IgProfile }>(
          `/api/instagram/profile?username=${encodeURIComponent(username!)}`,
        );
        return res.profile;
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    enabled: !!username,
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    retry: false,
  });
}

function Avatar({ src, size }: { src: string | null | undefined; size: number }) {
  return src ? (
    <img src={src} alt="" width={size} height={size} className="rounded-full object-cover shrink-0" style={{ width: size, height: size }} />
  ) : (
    <span
      className="rounded-full bg-slate-200 text-slate-400 inline-flex items-center justify-center shrink-0"
      style={{ width: size, height: size }}
    >
      <Users size={Math.round(size * 0.55)} />
    </span>
  );
}

function CollaboratorChip({ username, onRemove }: { username: string; onRemove: () => void }) {
  const { data } = useIgProfile(username);
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 text-slate-700 text-xs font-medium pl-1 pr-1 py-1">
      <Avatar src={data?.avatar} size={20} />
      @{username}
      <button
        type="button"
        onClick={onRemove}
        className="inline-flex h-4 w-4 items-center justify-center rounded-full hover:bg-slate-200 text-slate-500 hover:text-red-600 transition"
        aria-label={`Quitar a @${username}`}
      >
        <X size={10} />
      </button>
    </span>
  );
}

export function CollaboratorsInput(props: { value: string[]; onChange: (v: string[]) => void }) {
  const { value, onChange } = props;
  const [open, setOpen] = useState(value.length > 0);
  const [draft, setDraft] = useState('');
  const [lookupName, setLookupName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const hasValue = value.length > 0;

  // Collaborators loaded later (editing a post) → show them.
  useEffect(() => {
    if (hasValue) setOpen(true);
  }, [hasValue]);

  // Look the account up once the user stops typing.
  useEffect(() => {
    const name = draft.trim().replace(/^@/, '');
    if (!USERNAME.test(name) || value.some((v) => v.toLowerCase() === name.toLowerCase())) {
      setLookupName(null);
      return;
    }
    const t = setTimeout(() => setLookupName(name), 450);
    return () => clearTimeout(t);
  }, [draft, value]);

  const lookup = useIgProfile(lookupName);

  function add(name: string) {
    if (value.length >= MAX_COLLABORATORS) {
      setError(`Instagram permite hasta ${MAX_COLLABORATORS} colaboradores.`);
      return;
    }
    if (!value.some((v) => v.toLowerCase() === name.toLowerCase())) onChange([...value, name]);
    setDraft('');
    setLookupName(null);
    setError(null);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      setDraft('');
      return;
    }
    if (e.key !== 'Enter' && e.key !== ',' && e.key !== ' ') return;
    e.preventDefault();
    const name = draft.trim().replace(/^@/, '');
    if (!name) return;
    if (!USERNAME.test(name)) {
      setError('Escribe el nombre de usuario de Instagram, sin espacios.');
      return;
    }
    if (lookupName?.toLowerCase() !== name.toLowerCase() || lookup.isLoading) return; // still checking
    if (lookup.data) add(lookup.data.username);
    else if (lookup.isError) add(name);
  }

  return (
    <div className="rounded-xl border border-slate-200">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-left"
      >
        <Users size={14} className="text-slate-400" />
        <span className="text-sm font-medium text-slate-700">Colaboradores de Instagram</span>
        {hasValue && (
          <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-brand-50 text-brand-700 text-[11px] font-semibold">
            {value.length}
          </span>
        )}
        <span className="ml-auto text-[11px] text-slate-400">Opcional</span>
        <ChevronDown size={16} className={cn('text-slate-400 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="px-3 pb-3">
          <p className="text-[11px] text-slate-400 mb-2">
            Les llega una invitación; si la aceptan, el post sale también en su perfil. Hasta {MAX_COLLABORATORS}.
          </p>
          {hasValue && (
            <div className="flex flex-wrap items-center gap-1.5 mb-2">
              {value.map((u) => (
                <CollaboratorChip key={u} username={u} onRemove={() => onChange(value.filter((v) => v !== u))} />
              ))}
            </div>
          )}
          {value.length < MAX_COLLABORATORS && (
            <div className="relative">
              <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-slate-400">@</span>
              <input
                value={draft}
                onChange={(e) => {
                  setDraft(e.target.value.replace(/@/g, ''));
                  setError(null);
                }}
                onKeyDown={onKeyDown}
                placeholder="nombre de usuario"
                aria-label="Nombre de usuario de Instagram"
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                className="w-full text-sm pl-6 pr-2.5 py-1.5 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
              {lookupName && (
                <div className="absolute z-20 left-0 right-0 mt-1 rounded-lg border border-slate-200 bg-white shadow-lg p-1">
                  {lookup.isLoading ? (
                    <div className="flex items-center gap-2 px-2 py-2 text-xs text-slate-500">
                      <Loader2 size={12} className="animate-spin" /> Buscando @{lookupName}…
                    </div>
                  ) : lookup.data ? (
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => add(lookup.data!.username)}
                      className="w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-slate-50 text-left transition"
                    >
                      <Avatar src={lookup.data.avatar} size={32} />
                      <span className="min-w-0">
                        <span className="flex items-center gap-1 text-sm font-medium text-slate-900">
                          @{lookup.data.username}
                          {lookup.data.isVerified && <BadgeCheck size={14} className="text-brand-600 shrink-0" />}
                        </span>
                        {lookup.data.fullName && (
                          <span className="block truncate text-xs text-slate-500">{lookup.data.fullName}</span>
                        )}
                      </span>
                      <span className="ml-auto text-xs font-medium text-brand-600 shrink-0">Añadir</span>
                    </button>
                  ) : lookup.data === null ? (
                    <div className="px-2 py-2 text-xs text-slate-500">No hay ninguna cuenta @{lookupName} en Instagram.</div>
                  ) : (
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => add(lookupName)}
                      className="w-full flex items-center justify-between gap-2 px-2 py-2 rounded-md hover:bg-slate-50 text-left text-xs transition"
                    >
                      <span className="text-slate-500">No hemos podido comprobar la cuenta.</span>
                      <span className="font-medium text-brand-600 shrink-0">Añadir @{lookupName}</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
          {error && <p className="mt-1.5 text-xs text-red-600">{error}</p>}
        </div>
      )}
    </div>
  );
}
